import React, { useRef, useState, useEffect } from 'react'
import { useConfiguratorContext, DraggableItem } from '@augmatica/iframe-interface-react'

import lfcDimensionsVisibilityJson from '../../data/lfcDimensionsVisibility.json'
import { getFilteredItems } from '../../helpers/getFilteredItems'
import { getPropsForUpdate } from '../../helpers/getPropsForUpdate'
import { getVisibleLfcDimensionsNames } from '../../helpers/getVisibleLfcDimensionsNames'
import { isAnyItem } from '../../helpers/isAnyItem'
import { isLfcItem } from '../../helpers/isLfcItem'
import { handleFilterChange } from '../../store/items/itemsSlice'
import { getInfoModalPayload } from '../../helpers/getInfoModalPayload'
import { handleSelectedItem, handleSlotOptions } from '../../store/selectedItem/selectedItemSlice'
import { handleItemInfoModalClose, handleItemInfoModalOpen, setIsMobileDrawerOpen } from '../../store/ui/uiSlice'
import Card from '../Card/Card'
import Heading from '../Heading/Heading'
import Modal from '../Modal/Modal'
import Select from '../Select/Select'
import * as commonStyles from '../styles'
import * as styles from './styles'
import { isMobile } from '../../helpers/isMobile'
import { useAppDispatch, useAppSelector } from '../../hooks'
import * as DynamakerContract from '../../integration/DynamakerContract'

const Styled = {
  ...commonStyles,
  ...styles,
}

interface Props {
  itemType: DynamakerContract.ItemGroup
}

const ItemPanel: React.FC<Props> = ({ itemType }) => {
  const dispatch = useAppDispatch()

  const gridRef = useRef<HTMLDivElement>(null)
  const selectedItemRef = useRef<HTMLDivElement>(null)

  const configurator = useConfiguratorContext()

  const { assetBaseUrl, content, langMarketCode } = useAppSelector((state) => state.init)
  const { erpItems, lfcItems, renderItems, filterOptions } = useAppSelector((state) => state.items)
  const { selectedItem, slotOptions } = useAppSelector((state) => state.selectedItem)
  const { itemInfoModal } = useAppSelector((state) => state.ui)

  const [filteredItems, setFilteredItems] = useState(() =>
    getFilteredItems(renderItems[itemType] ?? [], slotOptions ?? [], filterOptions[itemType])
  )

  useEffect(() => {
    setFilteredItems(getFilteredItems(renderItems[itemType] ?? [], slotOptions ?? [], filterOptions[itemType]))
  }, [slotOptions, filterOptions])

  useEffect(() => {
    if (selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      })
    } else {
      gridRef.current?.scroll({
        behavior: 'smooth',
        left: 0,
      })
    }
  }, [filteredItems])

  return (
    <>
      <Styled.ItemPanel>
        {filterOptions[itemType] && (
          <div className="filters">
            {Object.entries(filterOptions[itemType]).map(([key, options]) => (
              <Select
                key={key}
                type="filter"
                id={`${itemType}--${key}`}
                value={options.find((option) => option.isActive)?.value as any} // TODO
                onChange={(event) => {
                  dispatch(
                    handleFilterChange({
                      itemType,
                      updated: { [key]: event.target.value },
                    })
                  )
                }}
                options={options.map((option) => ({
                  key: option.value,
                  value: option.value,
                  disabled: false,
                }))}
                formatterForOption={
                  (value) =>
                    content.filters[key]?.options?.[value!] ? content.filters[key].options[value!].title : value // TODO: value can be undefined
                }
                label={content.filters[key] ? content.filters[key].title : key}
                dangerouslySetInfo={content.filters[key] ? content.filters[key].description : null}
              />
            ))}
          </div>
        )}

        <Styled.Grid $columns={2} ref={gridRef}>
          {filteredItems.flatMap(({ _internalId, _editorial, item: itemType, erpItemNumber, props: itemProps }) => {
            const erpItem = erpItems.find((erpItem) => erpItem.Sku === erpItemNumber)

            const isSelectedItem =
              itemType === selectedItem.item &&
              erpItemNumber === selectedItem.erpItemNumber &&
              (!itemProps.configData ||
                !selectedItem.props?.configData ||
                // for mallis preset
                itemProps.configData.namedConfigCode === selectedItem.props.configData.namedConfigCode) &&
              (erpItemNumber !== null ||
                selectedItem.erpItemNumber !== null ||
                // for module without item ("None")
                itemProps.moduleHeight === selectedItem.props?.moduleHeight)

            return _internalId
              ? [
                  <div
                    key={_internalId}
                    data-internal-id={_internalId}
                    {...(isSelectedItem ? { ref: selectedItemRef } : {})}
                  >
                    <DraggableItem item={itemType} props={itemProps}>
                      <Card
                        isSelected={isSelectedItem}
                        onClick={() => {
                          if (!isAnyItem(selectedItem) || isSelectedItem) {
                            configurator.postMessage('place-item', {
                              item: itemType,
                              props: itemProps,
                            })
                            return
                          }

                          dispatch(
                            handleSelectedItem({
                              erpItemNumber,
                              id: selectedItem.id,
                              item: itemType,
                              props: itemProps,
                            })
                          )

                          const isSameTypeOfItem =
                            selectedItem.item === itemType && selectedItem.erpItemNumber && erpItemNumber
                          const isSameModuleWidth =
                            'openingWidth' in selectedItem.props &&
                            selectedItem.props &&
                            selectedItem.props.openingWidth === itemProps.openingWidth

                          if (isSameTypeOfItem && isSameModuleWidth) {
                            if (selectedItem.props) {
                              const propsForUpdate = getPropsForUpdate(selectedItem.props as any, itemProps) // TODO
                              configurator.updateItem(selectedItem.item, selectedItem.id, propsForUpdate as any) // TODO
                            }
                          } else {
                            configurator
                              .replaceItem(selectedItem.item, selectedItem.id, itemType, itemProps)
                              .then((value) => {
                                const { slotOptions, ...selectedItemAfterReplacement } = value

                                dispatch(handleSlotOptions({ slotOptions }))
                                dispatch(handleSelectedItem(selectedItemAfterReplacement))
                              })
                              .catch((error) => {
                                console.error(error)
                              })
                          }
                          if (isMobile()) {
                            dispatch(setIsMobileDrawerOpen(false))
                          }
                        }}
                        onInfoClick={() => {
                          dispatch(
                            handleItemInfoModalOpen(
                              getInfoModalPayload(
                                _editorial,
                                assetBaseUrl,
                                langMarketCode,
                                getErpItemAttributes(erpItem, content, itemProps, lfcItems),
                                erpItem
                              )
                            )
                          )
                        }}
                        mediaAlt={
                          erpItem
                            ? erpItem.Title || erpItem.Sku
                            : _editorial?.title
                              ? _editorial.title[langMarketCode]
                              : ''
                        }
                        mediaImage={
                          _editorial?.noppeImageId
                            ? `${assetBaseUrl}/mediaarchive/s2/images/${
                                erpItem ? erpItem.Title || erpItem.Sku : _internalId
                              }/${_editorial.noppeImageId}?d=240x240`
                            : `${assetBaseUrl}/mediaarchive/s/images/${
                                erpItem?.Title?.replaceAll('/', '-').replaceAll(' ', '-') || erpItem?.Sku
                              }/${erpItem?.Sku}?d=240x240`
                        }
                        title={
                          erpItem
                            ? erpItem.Title || erpItem.Sku
                            : _editorial?.title
                              ? (_editorial?.title[langMarketCode] ?? '')
                              : ''
                        }
                        titleLong={
                          erpItem?.TitleLong && _editorial
                            ? `${erpItem.TitleLong}, ${_editorial.titleExtension[langMarketCode]}`
                            : erpItem?.TitleLong
                              ? erpItem.TitleLong
                              : !erpItem && _editorial?.titleLong
                                ? _editorial.titleLong[langMarketCode]
                                : ''
                        }
                      ></Card>
                    </DraggableItem>
                  </div>,
                ]
              : []
          })}
        </Styled.Grid>

        {filteredItems.length === 0 && (
          <div className="instruction">
            <span className="text">
              <strong>{content.items[itemType].notAvailableForSlot.description}</strong>
            </span>
          </div>
        )}
      </Styled.ItemPanel>

      <Modal
        isOpen={itemInfoModal.isOpen}
        onRequestClose={() => {
          dispatch(handleItemInfoModalClose())
        }}
        contentLabel={content.other.moreInformation}
      >
        <Styled.ModalItemInfo>
          <img src={itemInfoModal.content.imgSrc} alt={itemInfoModal.content.title} />
          <Heading level={2}>{itemInfoModal.content.title}</Heading>
          {itemInfoModal.content.titleLong && <Heading level={3}>{itemInfoModal.content.titleLong}</Heading>}
          {itemInfoModal.content.descriptionShort && <p>{itemInfoModal.content.descriptionShort}</p>}
          {itemInfoModal.content.erpItemNumber && (
            <p>
              {content.other.artNo}: {itemInfoModal.content.erpItemNumber}
            </p>
          )}
          {itemInfoModal.content.attributes?.length ? (
            <table>
              <tbody>
                {itemInfoModal.content.attributes.map((attribute: any, index) => (
                  <tr key={`${index}-${attribute.erpItemNumber || 'None'}`}>
                    <td>{attribute.Title}</td>
                    <td>{attribute.Value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </Styled.ModalItemInfo>
      </Modal>
    </>
  )
}

export default ItemPanel

const getErpItemAttributes = (erpItem: any, content: any, itemProps: any, lfcItems: any[]): any[] => {
  if (!erpItem) return []

  const attributes = []

  if (isLfcItem({ props: itemProps })) {
    const lfcItem = lfcItems.find(
      (lfcItem) =>
        // TODO lägg tillbaka när bugg rättats i Scensum
        // lfcItem.configuratorContextId === erpItem.contextId
        lfcItem.configuratorContextId === erpItem.Sku
    )

    if (lfcItem) {
      getVisibleLfcDimensionsNames(
        lfcItem.dimensions,
        lfcDimensionsVisibilityJson,
        'SE' // behöver språkhanteras framöver
      ).forEach((dimName) =>
        attributes.push({
          Title: dimName,
          Value: content.other.lfcDimensionIsEditable,
        })
      )
    }
  }

  attributes.push(...erpItem.Attributes)

  return attributes
}
